import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, InputGroup, Dropdown, Badge, Alert, Spinner, Modal } from 'react-bootstrap';
import { FaSearch, FaFilter, FaPlus, FaEye, FaEdit, FaUserMd, FaPhoneAlt, FaEnvelope, FaCalendarAlt, FaSort, FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaExchangeAlt, FaHome, FaPills, FaUserGraduate } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';

import patientRecordsService from '../../services/patientRecordsService';
import { selectInstitutionType, fetchInstitutionSettings } from '../../redux/slices/institutionSlice';
import { LoadingStates } from '../../components/ui/LoadingStates';
import SmartMedicationDashboard from './SmartMedicationDashboard';
import config from '../../config/config';
import '../componentCss/patientRecords.css';

// Enhanced Status Change Modal with Discharge Support
const StatusChangeModal = ({ show, onHide, onSubmit, patient, loading }) => {
    const [selectedStatus, setSelectedStatus] = useState('');
    const [reason, setReason] = useState('');
    const [dischargeDate, setDischargeDate] = useState('');
    const [dischargeTime, setDischargeTime] = useState('');
    const [dischargeReason, setDischargeReason] = useState('');

    const statusOptions = patientRecordsService.getStatusOptions();
    const isDischargeStatus = selectedStatus === 'discharged';

    // Reset discharge fields when modal opens or status changes
    React.useEffect(() => {
        if (show) {
            setSelectedStatus('');
            setReason('');
            setDischargeDate('');
            setDischargeTime('');
            setDischargeReason('');
        }
    }, [show]);

    React.useEffect(() => {
        if (!isDischargeStatus) {
            setDischargeDate('');
            setDischargeTime('');
            setDischargeReason('');
        } else {
            // Set default discharge date to today
            const today = new Date().toISOString().split('T')[0];
            setDischargeDate(today);
            // Set default discharge time to current time
            const now = new Date();
            const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
            setDischargeTime(currentTime);
        }
    }, [isDischargeStatus]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!selectedStatus) {
            toast.error('Please select a status');
            return;
        }

        if (isDischargeStatus && !dischargeReason.trim()) {
            toast.error('Please provide a discharge reason');
            return;
        }

        const statusData = {
            status: selectedStatus,
            reason: reason || undefined,
            ...(isDischargeStatus && {
                discharge_date: dischargeDate,
                discharge_time: dischargeTime,
                discharge_reason: dischargeReason
            })
        };

        onSubmit(statusData);
    };

    return (
        <Modal show={show} onHide={onHide} size="md">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaEdit className="me-2" />
                    Update Status: {patient?.full_name}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>New Status</Form.Label>
                        <Form.Select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            required
                        >
                            <option value="">Select Status</option>
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {/* Discharge-specific fields */}
                    {isDischargeStatus && (
                        <>
                            <hr />
                            <h6 className="text-primary mb-3">
                                <FaHome className="me-2" />
                                Discharge Information
                            </h6>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Discharge Date <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={dischargeDate}
                                            onChange={(e) => setDischargeDate(e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Discharge Time <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={dischargeTime}
                                            onChange={(e) => setDischargeTime(e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Discharge Reason <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={dischargeReason}
                                    onChange={(e) => setDischargeReason(e.target.value)}
                                    placeholder="Please provide the reason for discharge (e.g., Treatment completed, Transferred to another facility, etc.)"
                                    required
                                />
                            </Form.Group>
                        </>
                    )}

                    {/* General reason field for non-discharge status changes */}
                    {!isDischargeStatus && (
                        <Form.Group className="mb-3">
                            <Form.Label>Reason (Optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Reason for status change..."
                            />
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        variant={isDischargeStatus ? "success" : "primary"}
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? <Spinner size="sm" className="me-2" /> : <FaEdit className="me-2" />}
                        {isDischargeStatus ? 'Discharge Patient' : 'Update Status'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// Smart MedPass Dashboard Modal
const SmartMedPassModal = ({ show, onHide, patient }) => {
    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            fullscreen="lg-down"
            centered
            enforceFocus={false}
            restoreFocus={false}
            autoFocus={false}
            backdrop="static"
        >
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>
                    <FaPills className="me-2" />
                    Smart MedPass Dashboard - {patient?.full_name || patient?.name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0" style={{ minHeight: '600px' }}>
                {patient && (
                    <SmartMedicationDashboard
                        selectedPatientId={patient.id}
                        embeddedMode={true}
                    />
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 bg-light">
                <Button variant="secondary" onClick={onHide}>
                    <FaHome className="me-2" />
                    Back to Patient Records
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const PatientRecordsDashboard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Institution type check
    const institutionType = useSelector(selectInstitutionType);
    const institutionLoading = useSelector(state => state.institution?.loading);
    const isSchoolInstitution = institutionType === 'school';

    // State Management
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        service: '',
        assigned_to_me: false,
        registration_status: '',
        student_class: '',
        student_section: ''
    });
    const [pagination, setPagination] = useState({
        page: 1,
        per_page: 50,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });
    const [sortConfig, setSortConfig] = useState({
        sort_by: 'updated_at',
        sort_order: 'desc'
    });
    const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
    const [showFilters, setShowFilters] = useState(false);
    const [selectedPatients, setSelectedPatients] = useState(new Set());
    const [refreshing, setRefreshing] = useState(false);

    // Status change modal
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedPatientForAction, setSelectedPatientForAction] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Smart MedPass modal
    const [showSmartMedPassModal, setShowSmartMedPassModal] = useState(false);
    const [selectedPatientForMedPass, setSelectedPatientForMedPass] = useState(null);

    // Student class filter state (for school institutions)
    const [studentClasses, setStudentClasses] = useState([]);
    const [loadingClasses, setLoadingClasses] = useState(false);

    // Student section filter state (for school institutions)
    const [studentSections, setStudentSections] = useState([]);
    const [loadingSections, setLoadingSections] = useState(false);

    // Search state management
    const searchTimeoutRef = useRef(null);
    const [searching, setSearching] = useState(false);
    const [searchMessage, setSearchMessage] = useState('');
    const initialLoadRef = useRef(true);
    const isSearchUpdatingPaginationRef = useRef(false); // New ref to track search pagination updates

    // Load patients data
    const loadPatients = useCallback(async (showLoadingState = true) => {
        try {
            if (showLoadingState) {
                setLoading(true);
            } else {
                setRefreshing(true);
            }
            setError(null);

            const params = {
                page: pagination.page,
                per_page: pagination.per_page,
                search: searchQuery.trim(),
                ...filters,
                ...sortConfig
            };

            const response = await patientRecordsService.getPatientRecords(params);

            if (response.success) {
                // Format patients for display
                const formattedPatients = response.patients.map(patient =>
                    patientRecordsService.formatPatientForDisplay(patient)
                );

                setPatients(formattedPatients);
                setPagination({
                    page: response.pagination.page,
                    per_page: response.pagination.per_page,
                    total: response.pagination.total,
                    total_pages: response.pagination.total_pages,
                    has_next: response.pagination.has_next,
                    has_prev: response.pagination.has_prev
                });

                // Only show success toast for initial load, not for refreshes
                if (showLoadingState) {
                    toast.success(`${response.pagination.total} patient records loaded`);
                }
            } else {
                setError(response.error || 'Failed to load patient records');
                toast.error(response.error || 'Failed to load patient records');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            toast.error('An unexpected error occurred');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [filters, sortConfig]);

    // Execute search with specific query parameter
    const executeSearchWithQuery = useCallback(async (queryToSearch) => {
        try {
            setSearching(true);
            setSearchMessage('');
            setError(null);
            
            const params = {
                page: 1, // Always start from page 1 for new search
                per_page: pagination.per_page,
                search: queryToSearch.trim(),
                ...filters,
                ...sortConfig
            };

            const response = await patientRecordsService.getPatientRecords(params);
            
            if (response.success) {
                // Format patients for display (same as loadPatients)
                const formattedPatients = response.patients.map(patient =>
                    patientRecordsService.formatPatientForDisplay(patient)
                );

                setPatients(formattedPatients);
                setPagination({
                    page: response.pagination.page,
                    per_page: response.pagination.per_page,
                    total: response.pagination.total,
                    total_pages: response.pagination.total_pages,
                    has_next: response.pagination.has_next,
                    has_prev: response.pagination.has_prev
                });
                
                // Show search results message
                const resultCount = response.pagination.total;
                const searchTerm = queryToSearch.trim();
                if (resultCount === 0) {
                    setSearchMessage(`No patients found for "${searchTerm}"`);
                } else {
                    setSearchMessage(`Found ${resultCount} patient${resultCount !== 1 ? 's' : ''} matching "${searchTerm}"`);
                }
            } else {
                setError(response.error || 'Failed to search patient records');
                toast.error(response.error || 'Failed to search patient records');
            }
        } catch (err) {
            setError('An unexpected error occurred during search');
            toast.error('An unexpected error occurred during search');
        } finally {
            setSearching(false);
        }
    }, [pagination.per_page, filters, sortConfig.sort_by, sortConfig.sort_order]);

    // Execute search with current searchQuery
    const executeSearch = useCallback(async () => {
        try {
            setSearching(true);
            setSearchMessage('');
            setError(null);
            
            const params = {
                page: 1, // Always start from page 1 for new search
                per_page: pagination.per_page,
                search: searchQuery.trim(),
                ...filters,
                ...sortConfig
            };

            const response = await patientRecordsService.getPatientRecords(params);
            
            if (response.success) {
                // Format patients for display (same as loadPatients)
                const formattedPatients = response.patients.map(patient =>
                    patientRecordsService.formatPatientForDisplay(patient)
                );

                setPatients(formattedPatients);
                setPagination({
                    page: response.pagination.page,
                    per_page: response.pagination.per_page,
                    total: response.pagination.total,
                    total_pages: response.pagination.total_pages,
                    has_next: response.pagination.has_next,
                    has_prev: response.pagination.has_prev
                });
                
                // Show search results message
                const resultCount = response.pagination.total;
                const searchTerm = searchQuery.trim();
                if (resultCount === 0) {
                    setSearchMessage(`No patients found for "${searchTerm}"`);
                } else {
                    setSearchMessage(`Found ${resultCount} patient${resultCount !== 1 ? 's' : ''} matching "${searchTerm}"`);
                }
            } else {
                setError(response.error || 'Failed to search patient records');
                toast.error(response.error || 'Failed to search patient records');
            }
        } catch (err) {
            setError('An unexpected error occurred during search');
            toast.error('An unexpected error occurred during search');
        } finally {
            setSearching(false);
        }
    }, [searchQuery, pagination.per_page, filters, sortConfig.sort_by, sortConfig.sort_order]);

    // Initial load
    useEffect(() => {
        loadPatients(true);
        initialLoadRef.current = false;
        
        // Cleanup timeout on unmount
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
        };
    }, []);

    // Monitor pagination changes and reload data
    // Note: searchQuery is intentionally excluded from dependencies to prevent
    // conflicts with search functionality that updates pagination state
    useEffect(() => {
        // Skip initial load (handled by initial useEffect above)
        if (initialLoadRef.current) {
            return;
        }
        
        // Only apply pagination changes if not currently searching
        if (isSearchUpdatingPaginationRef.current) {
            return;
        }
        
        // Call loadPatients directly to avoid dependency issues
        const loadData = async () => {
            try {
                setRefreshing(true);
                setError(null);

                const params = {
                    page: pagination.page,
                    per_page: pagination.per_page,
                    search: searchQuery.trim(),
                    ...filters,
                    ...sortConfig
                };

                const response = await patientRecordsService.getPatientRecords(params);

                if (response.success) {
                    // Format patients for display
                    const formattedPatients = response.patients.map(patient =>
                        patientRecordsService.formatPatientForDisplay(patient)
                    );

                    setPatients(formattedPatients);
                    setPagination({
                        page: response.pagination.page,
                        per_page: response.pagination.per_page,
                        total: response.pagination.total,
                        total_pages: response.pagination.total_pages,
                        has_next: response.pagination.has_next,
                        has_prev: response.pagination.has_prev
                    });
                } else {
                    setError(response.error || 'Failed to load patient records');
                }
            } catch (err) {
                setError('An unexpected error occurred');
            } finally {
                setRefreshing(false);
            }
        };
        
        loadData();
    }, [pagination.page, pagination.per_page, filters, sortConfig]); // Removed searchQuery to prevent conflicts with search


    // Search input handler with debounce and validation
    const handleSearchInput = useCallback((query) => {
        setSearchQuery(query);
        setSearching(false);
        setSearchMessage('');

        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = null;
        }

        // If query is empty, clear search and reset to show all patients
        if (!query || query.trim() === '') {
            setSearchMessage('');
            // Only reload if we were previously in search mode
            if (searchQuery) {
                setPagination(prev => ({ ...prev, page: 1 }));
                loadPatients(false);
            }
            return;
        }

        const trimmedQuery = query.trim();
        
        // Check minimum character requirement
        if (trimmedQuery.length < 2) {
            setSearchMessage(`Type at least 2 characters to search (${trimmedQuery.length}/2)`);
            // Don't trigger any API call for short queries - just show the message
            return;
        }

        // Show waiting message but don't block input
        setSearchMessage(`Will search for "${trimmedQuery}" when you stop typing...`);

        // Set new timeout for debounced search (1000ms)
        searchTimeoutRef.current = setTimeout(() => {
            // Use the trimmed query directly to avoid state timing issues
            executeSearchWithQuery(trimmedQuery);
            searchTimeoutRef.current = null;
        }, 1000); // 1000ms debounce delay - wait for user to stop typing
    }, [executeSearchWithQuery, loadPatients, searchQuery]);

    // Manual search handler (bypasses character limit)
    const handleManualSearch = useCallback(() => {
        // Clear any pending debounced search
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = null;
        }

        const trimmedQuery = searchQuery.trim();
        
        if (!trimmedQuery) {
            // If empty, load all patients
            setPagination(prev => ({ ...prev, page: 1 }));
            loadPatients(false);
            return;
        }

        // Execute search immediately, regardless of character count
        executeSearch();
    }, [searchQuery, executeSearch, loadPatients]);

    // Handle Enter key press in search input
    const handleSearchKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleManualSearch();
        }
    }, [handleManualSearch]);

    // Filter handler
    const handleFilterChange = useCallback((filterKey, value) => {
        setFilters(prev => ({
            ...prev,
            [filterKey]: value
        }));
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    // Load student classes for school institutions
    const loadStudentClasses = useCallback(async () => {
        if (!isSchoolInstitution) return;

        try {
            setLoadingClasses(true);
            const response = await patientRecordsService.getStudentClasses();

            if (response.success) {
                setStudentClasses(response.classes);
            } else {
                console.warn('Failed to load student classes:', response.error);
                setStudentClasses([]);
            }
        } catch (error) {
            console.error('Error loading student classes:', error);
            setStudentClasses([]);
        } finally {
            setLoadingClasses(false);
        }
    }, [isSchoolInstitution]);

    // Fetch institution settings on mount to ensure we have current institution type
    useEffect(() => {
        dispatch(fetchInstitutionSettings());
    }, [dispatch]);

    // Effect to load student classes on mount and when institution type changes
    useEffect(() => {
        loadStudentClasses();
    }, [loadStudentClasses]);

    // Load student sections for filtering (school institutions)
    const loadStudentSections = useCallback(async () => {
        if (!isSchoolInstitution) return;

        try {
            setLoadingSections(true);
            const response = await patientRecordsService.getStudentSections();

            if (response.success) {
                setStudentSections(response.sections);
            } else {
                console.warn('Failed to load student sections:', response.error);
                setStudentSections([]);
            }
        } catch (error) {
            console.error('Error loading student sections:', error);
            setStudentSections([]);
        } finally {
            setLoadingSections(false);
        }
    }, [isSchoolInstitution]);

    // Effect to load student sections on mount and when institution type changes
    useEffect(() => {
        loadStudentSections();
    }, [loadStudentSections]);

    // Apply filters
    const applyFilters = useCallback(() => {
        loadPatients(false);
        setShowFilters(false);
        toast.success('Filters applied');
    }, [loadPatients]);

    // Clear filters
    const clearFilters = useCallback(() => {
        setFilters({
            status: '',
            service: '',
            assigned_to_me: false,
            registration_status: '',
            student_class: '',
            student_section: ''
        });
        setSearchQuery('');
        setPagination(prev => ({ ...prev, page: 1 }));
        toast.success('Filters cleared');
    }, []);

    // Pagination handlers
    const handlePageChange = useCallback((newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    }, []);

    // Sort handler
    const handleSort = useCallback((field) => {
        setSortConfig(prev => ({
            sort_by: field,
            sort_order: prev.sort_by === field && prev.sort_order === 'asc' ? 'desc' : 'asc'
        }));
    }, []);

    // Patient selection handlers
    const handlePatientSelect = useCallback((patientId, isSelected) => {
        setSelectedPatients(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(patientId);
            } else {
                newSet.delete(patientId);
            }
            return newSet;
        });
    }, []);

    const handleSelectAll = useCallback((isSelected) => {
        if (isSelected) {
            setSelectedPatients(new Set(patients.map(p => p.id)));
        } else {
            setSelectedPatients(new Set());
        }
    }, [patients]);

    // Navigation handlers
    const handleViewPatient = useCallback((patientId) => {
        navigate(`/patient-details/${patientId}`);
    }, [navigate]);

    const handleEditPatient = useCallback((patientId) => {
        navigate(`/edit-patient/${patientId}`);
    }, [navigate]);

    // Quick action handlers
    const handleStatusChange = useCallback((patient) => {
        setSelectedPatientForAction(patient);
        setShowStatusModal(true);
    }, []);

    const handleSmartMedPass = useCallback((patient) => {
        setSelectedPatientForMedPass(patient);
        setShowSmartMedPassModal(true);
    }, []);

    const handleQuickStatusChange = useCallback(async (patient, newStatus) => {
        // If trying to discharge, redirect to modal for proper discharge process
        if (newStatus === 'discharged') {
            setSelectedPatientForAction(patient);
            setShowStatusModal(true);
            return;
        }

        setActionLoading(true);
        try {
            const statusData = { status: newStatus };
            const response = await patientRecordsService.updatePatientStatus(patient.id, statusData);
            if (response.success) {
                toast.success(`Patient status updated to ${newStatus}`);
                await loadPatients(false);
            } else {
                toast.error(response.error || 'Failed to update status');
            }
        } catch (error) {
            console.error('Quick status change error:', error);
            toast.error('Failed to update patient status');
        } finally {
            setActionLoading(false);
        }
    }, [loadPatients]);

    // Quick form handlers
    const getQuickFormOptions = useCallback((patient) => {
        const options = [];
        const patientStatus = patient.care_status?.status || patient.status;
        const patientDepartment = patient.department || patient.care_status?.department || 'general';
        const patientAge = calculateAge(patient.demographics?.date_of_birth || patient.date_of_birth);

        // Smart recommendations based on patient context
        if (patientStatus === 'pre_registered' || patientStatus === 4) {
            options.push({
                name: 'Admission Assessment',
                reason: 'New patient admission',
                priority: 'high',
                color: 'success',
                icon: FaUserMd,
                formType: 'admission'
            });
            options.push({
                name: 'Initial Evaluation',
                reason: 'Required for new patients',
                priority: 'high',
                color: 'warning',
                icon: FaEdit,
                formType: 'assessment'
            });
        } else if (patientStatus === 'active' || patientStatus === 1) {
            options.push({
                name: 'Daily Assessment',
                reason: 'Daily monitoring',
                priority: 'medium',
                color: 'info',
                icon: FaCalendarAlt,
                formType: 'daily'
            });
            options.push({
                name: 'Vital Signs',
                reason: 'Routine monitoring',
                priority: 'medium',
                color: 'primary',
                icon: FaUserMd,
                formType: 'vitals'
            });
            options.push({
                name: 'Medication Review',
                reason: 'Medication management',
                priority: 'medium',
                color: 'warning',
                icon: FaEdit,
                formType: 'medication'
            });
        }

        // Age-specific forms
        if (patientAge >= 65) {
            options.push({
                name: 'Geriatric Assessment',
                reason: 'Senior patient care',
                priority: 'medium',
                color: 'secondary',
                icon: FaUserMd,
                formType: 'geriatric'
            });
        } else if (patientAge < 18) {
            options.push({
                name: 'Pediatric Assessment',
                reason: 'Child patient care',
                priority: 'medium',
                color: 'info',
                icon: FaUserMd,
                formType: 'pediatric'
            });
        }

        // Department-specific forms
        if (patientDepartment !== 'general') {
            options.push({
                name: `${patientDepartment} Forms`,
                reason: `Department-specific forms`,
                priority: 'low',
                color: 'secondary',
                icon: FaEdit,
                formType: 'department'
            });
        }

        // Add department ID for better form matching
        if (patient.department_id) {
            options.push({
                name: `Department Forms`,
                reason: `Forms for ${patientDepartment} department`,
                priority: 'medium',
                color: 'info',
                icon: FaEdit,
                formType: 'department',
                department_id: patient.department_id
            });
        }

        return options.slice(0, 5); // Limit to 5 options
    }, []);

    const calculateAge = (birthDate) => {
        if (!birthDate) return 0;
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const handleQuickForm = useCallback((patient) => {
        // Navigate to patient forms hub with specific form type
        navigate(`/patient-details/${patient.id}`, {
            state: {
                activeTab: 'clinical-forms',
                // formType: form.formType,
                quickAction: true
            }
        });
    }, [navigate]);

    const handleViewAllForms = useCallback((patientId) => {
        navigate(`/patient-details/${patientId}`, {
            state: {
                activeTab: 'clinical-forms'
            }
        });
    }, [navigate]);

    // Action handlers
    const handleStatusUpdate = useCallback(async (statusData) => {
        setActionLoading(true);
        try {
            const response = await patientRecordsService.updatePatientStatus(selectedPatientForAction.id, statusData);
            if (response.success) {
                const statusLabel = statusData.status === 'discharged' ? 'discharged' : statusData.status;
                toast.success(`Patient status updated to ${statusLabel}`);
                setShowStatusModal(false);
                await loadPatients(false);
            } else {
                toast.error(response.error || 'Failed to update status');
            }
        } catch (error) {
            console.error('Status update error:', error);
            toast.error('Failed to update patient status');
        } finally {
            setActionLoading(false);
        }
    }, [selectedPatientForAction?.id, loadPatients]);

    // Memoized computed values
    const filteredPatientsCount = useMemo(() => patients.length, [patients]);
    const selectedCount = useMemo(() => selectedPatients.size, [selectedPatients]);
    const hasActiveFilters = useMemo(() => {
        return searchQuery || filters.status || filters.service || filters.assigned_to_me || filters.registration_status;
    }, [searchQuery, filters]);


    // Patient Card Component
    const PatientCard = React.memo(({ patient }) => {
        const statusConfig = patientRecordsService.getStatusBadgeConfig(patient.status);

        return (
            <Card className="patient-card h-100 shadow-sm border-0" style={{ transition: 'all 0.2s ease-in-out' }}>
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="patient-info flex-grow-1">
                            <h6 className="patient-name mb-1 fw-bold text-primary">
                                {patient.full_name || `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || 'Unnamed'}
                            </h6>
                            <p className="patient-mrn mb-1 text-muted small">
                                <strong>MRN:</strong> {patient.mrn}
                            </p>
                            {patient.emirates_id && (
                                <p className="patient-id mb-1 text-muted small">
                                    <strong>Emirates ID:</strong> {patient.emirates_id}
                                </p>
                            )}
                        </div>
                        <div className="patient-actions">
                            <Form.Check
                                type="checkbox"
                                checked={selectedPatients.has(patient.id)}
                                onChange={(e) => handlePatientSelect(patient.id, e.target.checked)}
                                className="mb-2 d-flex justify-content-end align-items-end"
                            />
                            <Badge bg={statusConfig.color} className="status-badge">
                                {statusConfig.icon} {statusConfig.label}
                            </Badge>
                        </div>
                    </div>

                    <div className="patient-details">
                        <Row className="mb-2">
                            <Col xs={4}>
                                <small className="text-muted">
                                    <strong>Age:</strong> {patient.age}
                                </small>
                            </Col>
                            <Col xs={4}>
                                <small className="text-muted">
                                    <strong>Gender:</strong> {patient.gender}
                                </small>
                            </Col>
                            <Col xs={4}>
                                <small className="text-muted">
                                    <strong>Nationality:</strong> {patient.nationality || 'N/A'}
                                </small>
                            </Col>
                        </Row>

                        {patient.phone && (
                            <div className="contact-info mb-2">
                                <small className="text-muted d-flex align-items-center">
                                    <FaPhoneAlt className="me-1" />
                                    {patient.phone}
                                </small>
                            </div>
                        )}

                        {patient.current_services && patient.current_services.length > 0 && (
                            <div className="services mb-2">
                                <small className="text-muted"><strong>Active Services:</strong></small>
                                <div className="mt-1">
                                    {patient.current_services.slice(0, 2).map((service, index) => (
                                        <Badge key={index} bg="info" className="me-1 mb-1 small">
                                            {service}
                                        </Badge>
                                    ))}
                                    {patient.current_services.length > 2 && (
                                        <Badge bg="secondary" className="me-1 mb-1 small">
                                            +{patient.current_services.length - 2} more
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        )}

                        <Row className="mb-2">
                            {patient.last_visit && (
                                <Col xs={6}>
                                    <small className="text-muted d-flex align-items-center">
                                        <FaCalendarAlt className="me-1" />
                                        Last Visit: {new Date(patient.last_visit).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    </small>
                                </Col>
                            )}
                            {patient.admission_date && (
                                <Col xs={6}>
                                    <small className="text-success d-flex align-items-center">
                                        <FaUserMd className="me-1" />
                                        Admitted: {new Date(patient.admission_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    </small>
                                </Col>
                            )}
                        </Row>

                        {patient.attending_physician && (
                            <div className="physician-info mb-2">
                                <small className="text-primary d-flex align-items-center">
                                    <FaUserMd className="me-1" />
                                    Attending: {patient.attending_physician}
                                </small>
                            </div>
                        )}
                    </div>

                    <div className="patient-actions-buttons mt-3">
                        {/* Quick Form Actions */}
                        <div className="quick-form-actions mb-2">
                            <Row className="g-1">
                                <Col xs={12}>
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            variant="primary"
                                            size="sm"
                                            className="w-100"
                                            // onClick={() => handleQuickEditPatient(patient.id)}
                                            onClick={() => handleQuickForm(patient)}
                                        >
                                            <FaEdit className="me-1" /> Quick Forms
                                        </Dropdown.Toggle>
                                        {/* <Dropdown.Menu>
                                            {getQuickFormOptions(patient).map((form, index) => (
                                                <Dropdown.Item
                                                    key={index}
                                                    onClick={() => handleQuickForm(patient, form)}
                                                    className="d-flex align-items-center"
                                                >
                                                    <form.icon className={`me-2 text-${form.color}`} />
                                                    <div>
                                                        <div className="fw-bold">{form.name}</div>
                                                        <small className="text-muted">{form.reason}</small>
                                                    </div>
                                                    {form.priority === 'high' && (
                                                        <Badge bg="danger" className="ms-auto small">High</Badge>
                                                    )}
                                                </Dropdown.Item>
                                            ))}
                                            <Dropdown.Divider />
                                            <Dropdown.Item
                                                onClick={() => handleViewAllForms(patient.id)}
                                                className="text-primary"
                                            >
                                                <FaEye className="me-2" />
                                                View All Forms
                                            </Dropdown.Item>
                                        </Dropdown.Menu> */}
                                    </Dropdown>
                                </Col>
                            </Row>
                        </div>

                        {/* Regular Actions */}
                        <Row className="g-1 d-flex justify-content-center">
                            <Col xs={6}>
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => handleViewPatient(patient.id)}
                                    className="w-100"
                                >
                                    <FaEye className="me-1" /> View
                                </Button>
                            </Col>
                            <Col xs={6}>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => handleEditPatient(patient.id)}
                                    className="w-100"
                                >
                                    <FaEdit className="me-1" /> Edit
                                </Button>
                            </Col>
                            <Col xs={6}>
                                <Button
                                    size="sm"
                                    variant="outline-success"
                                    onClick={() => handleSmartMedPass(patient)}
                                    className="w-100"
                                >
                                    <FaPills className="me-1" /> Smart MedPass
                                </Button>
                            </Col>
                            <Col xs={6}>
                                <Button
                                    size="sm"
                                    variant="outline-warning"
                                    onClick={() => handleStatusChange(patient)}
                                    className="w-100"
                                >
                                    <FaEdit className="me-1" /> Status
                                </Button>
                            </Col>
                            {/* <Col xs={6}>
                                <Dropdown>
                                    <Dropdown.Toggle
                                        variant="outline-info"
                                        size="sm"
                                        className="w-100"
                                        disabled={patient.status === 'discharged'}
                                    >
                                        <FaExchangeAlt className="me-1" /> Quick Actions
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item
                                            onClick={() => handleQuickStatusChange(patient, 'active')}
                                            disabled={patient.status === 'active'}
                                        >
                                            <Badge bg="success" className="me-2">✅</Badge> Set Active
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            onClick={() => handleQuickStatusChange(patient, 'on_hold')}
                                            disabled={patient.status === 'on_hold'}
                                        >
                                            <Badge bg="info" className="me-2">⏸️</Badge> Set On Hold
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            onClick={() => handleQuickStatusChange(patient, 'transferred')}
                                            disabled={patient.status === 'transferred'}
                                        >
                                            <Badge bg="warning" className="me-2">🔄</Badge> Transfer
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            onClick={() => handleQuickStatusChange(patient, 'discharged')}
                                            disabled={patient.status === 'discharged'}
                                        >
                                            <Badge bg="secondary" className="me-2">🏠</Badge> Discharge
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Col> */}
                            {patient.registration_status === 'pre_registered' && (
                                <Col xs={12} pt-3>
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        className="w-100"
                                        onClick={() => navigate(`/complete-registration/${patient.id}`)}
                                    >
                                        <FaUserMd className="me-1" /> Complete Registration
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    </div>
                </Card.Body>
            </Card>
        );
    });

    // Render loading state
    if (loading && !refreshing) {
        return (
            <>

                <Container fluid className="p-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Loading patient records...</p>
                        </div>
                    </div>
                </Container>
            </>
        );
    }

    return (
        <>

            <Container fluid className="patient-records-dashboard p-4">
                {/* Header Section */}
                <Row className="mb-4">
                    <Col>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="page-title mb-1">Patient Records</h2>
                                <p className="text-muted mb-0">
                                    {pagination.total} total patients
                                    {hasActiveFilters && ` (${filteredPatientsCount} filtered)`}
                                    {selectedCount > 0 && ` • ${selectedCount} selected`}
                                </p>
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => loadPatients(false)}
                                    disabled={refreshing}
                                >
                                    {refreshing ? <Spinner size="sm" /> : 'Refresh'}
                                </Button>
                                <Link to="/add-patientCredentials">
                                    <Button variant="primary">
                                        <FaPlus className="me-2" />
                                        Add Patient
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Search and Filter Section */}
                <Row className="mb-4">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-3">
                                <Row className="align-items-center">
                                    <Col md={6}>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FaSearch />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by name, MRN, Emirates ID, phone... (min 2 characters)"
                                                value={searchQuery}
                                                onChange={(e) => handleSearchInput(e.target.value)}
                                                onKeyPress={handleSearchKeyPress}
                                            />
                                            {searchQuery && (
                                                <InputGroup.Text 
                                                    role="button" 
                                                    onClick={() => {
                                                        handleSearchInput('');
                                                        setSearchMessage('');
                                                        loadPatients(false);
                                                    }}
                                                    title="Clear search"
                                                    style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                                    className="text-danger hover-bg-light"
                                                >
                                                    ✕
                                                </InputGroup.Text>
                                            )}
                                            <Button 
                                                variant="primary" 
                                                onClick={handleManualSearch}
                                                disabled={searching}
                                                title="Search now"
                                            >
                                                {searching ? <Spinner size="sm" /> : 'Search'}
                                            </Button>
                                        </InputGroup>
                                        {searchMessage && (
                                            <div className={`mt-2 small d-flex align-items-center ${searching ? 'text-primary' : 'text-muted'}`}>
                                                {searching && <Spinner size="sm" className="me-2" />}
                                                {searchMessage}
                                            </div>
                                        )}
                                    </Col>
                                    <Col md={6} className="d-flex justify-content-end gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => setShowFilters(!showFilters)}
                                            className={hasActiveFilters ? 'text-primary border-primary' : ''}
                                        >
                                            <FaFilter className="me-1" />
                                            Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v && v !== false).length + (searchQuery ? 1 : 0)})`}
                                        </Button>
                                        {hasActiveFilters && (
                                            <Button variant="outline-danger" onClick={clearFilters}>
                                                Clear All
                                            </Button>
                                        )}
                                        <Dropdown>
                                            <Dropdown.Toggle variant="outline-secondary" size="sm">
                                                View: {viewMode === 'cards' ? 'Cards' : 'Table'}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => setViewMode('cards')}>
                                                    Cards View
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setViewMode('table')}>
                                                    Table View
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </Col>
                                </Row>

                                {/* Active Filter Badges */}
                                {(filters.status || filters.service || filters.student_class || filters.assigned_to_me || filters.registration_status) && (
                                    <Row className="mt-2">
                                        <Col>
                                            <div className="d-flex flex-wrap gap-2">
                                                {filters.status && (
                                                    <Badge
                                                        bg="primary"
                                                        className="px-3 py-2"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleFilterChange('status', '')}
                                                    >
                                                        Status: {filters.status}
                                                        <span className="ms-2">×</span>
                                                    </Badge>
                                                )}
                                                {filters.service && (
                                                    <Badge
                                                        bg="info"
                                                        className="px-3 py-2"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleFilterChange('service', '')}
                                                    >
                                                        Service: {filters.service}
                                                        <span className="ms-2">×</span>
                                                    </Badge>
                                                )}
                                                {isSchoolInstitution && filters.student_class && (
                                                    <Badge
                                                        bg="success"
                                                        className="px-3 py-2"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleFilterChange('student_class', '')}
                                                    >
                                                        Class: {filters.student_class}
                                                        <span className="ms-2">×</span>
                                                    </Badge>
                                                )}
                                                {isSchoolInstitution && filters.student_section && (
                                                    <Badge
                                                        bg="success"
                                                        className="px-3 py-2"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleFilterChange('student_section', '')}
                                                    >
                                                        Section: {filters.student_section}
                                                        <span className="ms-2">×</span>
                                                    </Badge>
                                                )}
                                                {filters.registration_status && (
                                                    <Badge
                                                        bg="warning"
                                                        className="px-3 py-2"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleFilterChange('registration_status', '')}
                                                    >
                                                        Registration: {filters.registration_status}
                                                        <span className="ms-2">×</span>
                                                    </Badge>
                                                )}
                                                {filters.assigned_to_me && (
                                                    <Badge
                                                        bg="secondary"
                                                        className="px-3 py-2"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleFilterChange('assigned_to_me', false)}
                                                    >
                                                        Assigned to me
                                                        <span className="ms-2">×</span>
                                                    </Badge>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                )}

                                {/* Advanced Filters */}
                                {showFilters && (
                                    <Row className="mt-3 pt-3 border-top">
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold">Status</Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={filters.status}
                                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                                >
                                                    <option value="">All Statuses</option>
                                                    <option value="active">Active</option>
                                                    <option value="discharged">Discharged</option>
                                                    <option value="transferred">Transferred</option>
                                                    <option value="on_hold">On Hold</option>
                                                    <option value="deceased">Deceased</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        {/* <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold">Service</Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={filters.service}
                                                    onChange={(e) => handleFilterChange('service', e.target.value)}
                                                >
                                                    <option value="">All Services</option>
                                                    <option value="homecare">Home Care</option>
                                                    <option value="dialysis">Dialysis</option>
                                                    <option value="rehabilitation">Rehabilitation</option>
                                                    <option value="consultation">Consultation</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col> */}
                                        {/* <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold">Registration</Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={filters.registration_status}
                                                    onChange={(e) => handleFilterChange('registration_status', e.target.value)}
                                                >
                                                    <option value="">All</option>
                                                    <option value="completed">Completed</option>
                                                    <option value="pre_registered">Pre-registered</option>
                                                    <option value="pending">Pending</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col> */}
                                        {/* Student Class Filter - Only for School Institutions */}
                                        {isSchoolInstitution && (
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">
                                                        <FaUserGraduate className="me-2" />
                                                        Student Class
                                                    </Form.Label>
                                                    <Form.Select
                                                        size="sm"
                                                        value={filters.student_class}
                                                        onChange={(e) => {
                                                            handleFilterChange('student_class', e.target.value);
                                                        }}
                                                        disabled={loadingClasses || institutionLoading}
                                                    >
                                                        <option value="">All Classes</option>
                                                        {studentClasses.map(classValue => (
                                                            <option key={classValue} value={classValue}>
                                                                {classValue}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {(loadingClasses || institutionLoading) && (
                                                        <Form.Text className="text-muted">
                                                            <Spinner size="sm" className="me-1" />
                                                            Loading classes...
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        )}
                                        {/* Student Section Filter - Only for School Institutions */}
                                        {isSchoolInstitution && (
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">
                                                        <FaUserGraduate className="me-2" />
                                                        Student Section
                                                    </Form.Label>
                                                    <Form.Select
                                                        size="sm"
                                                        value={filters.student_section}
                                                        onChange={(e) => {
                                                            handleFilterChange('student_section', e.target.value);
                                                        }}
                                                        disabled={loadingSections || institutionLoading}
                                                    >
                                                        <option value="">All Sections</option>
                                                        {studentSections.map(sectionValue => (
                                                            <option key={sectionValue} value={sectionValue}>
                                                                {sectionValue}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {(loadingSections || institutionLoading) && (
                                                        <Form.Text className="text-muted">
                                                            <Spinner size="sm" className="me-1" />
                                                            Loading sections...
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        )}
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold">Assignment</Form.Label>
                                                <Form.Check
                                                    type="checkbox"
                                                    label="Assigned to me only"
                                                    checked={filters.assigned_to_me}
                                                    onChange={(e) => handleFilterChange('assigned_to_me', e.target.checked)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} className="mt-2">
                                            <Button variant="primary" size="sm" onClick={applyFilters} className="me-2">
                                                Apply Filters
                                            </Button>
                                            <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                                                Clear
                                            </Button>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Error Display */}
                {error && (
                    <Row className="mb-4">
                        <Col>
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                <Alert.Heading>Error Loading Patients</Alert.Heading>
                                <p>{error}</p>
                                <Button variant="outline-danger" onClick={() => loadPatients(true)}>
                                    Try Again
                                </Button>
                            </Alert>
                        </Col>
                    </Row>
                )}

                {/* Bulk Actions */}
                {selectedCount > 0 && (
                    <Row className="mb-3">
                        <Col>
                            <Card className="bg-light border-0">
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>
                                            <strong>{selectedCount}</strong> patient{selectedCount !== 1 ? 's' : ''} selected
                                        </span>
                                        <div className="d-flex gap-2">
                                            <Button variant="outline-primary" size="sm">
                                                Export Selected
                                            </Button>
                                            <Button variant="outline-secondary" size="sm">
                                                Assign Staff
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => setSelectedPatients(new Set())}
                                            >
                                                Clear Selection
                                            </Button>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Patients Grid/Table */}
                {viewMode === 'cards' ? (
                    /* Cards View */
                    <Row>
                        {patients.length > 0 ? (
                            patients.map((patient, index) => (
                                <Col key={patient.id} lg={4} md={6} sm={12} className="mb-4">
                                    <PatientCard patient={patient} />
                                </Col>
                            ))
                        ) : (
                            <Col>
                                <Card className="text-center py-5">
                                    <Card.Body>
                                        <h5 className="text-muted">No patients found</h5>
                                        <p className="text-muted">
                                            {hasActiveFilters
                                                ? 'Try adjusting your search criteria or filters.'
                                                : 'No patient records available in the system.'
                                            }
                                        </p>
                                        {hasActiveFilters && (
                                            <Button variant="outline-primary" onClick={clearFilters}>
                                                Clear Filters
                                            </Button>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>
                ) : (
                    /* Table View - TODO: Implement table view */
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="text-center py-5">
                                    <h5 className="text-muted">Table View</h5>
                                    <p className="text-muted">Table view is coming soon!</p>
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => setViewMode('cards')}
                                    >
                                        Switch to Cards View
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                    <Row className="mt-4">
                        <Col>
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="pagination-info">
                                            <small className="text-muted">
                                                Showing {Math.min((pagination.page - 1) * pagination.per_page + 1, pagination.total)} to{' '}
                                                {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
                                                {pagination.total} results
                                            </small>
                                        </div>
                                        <div className="pagination-controls d-flex align-items-center gap-2">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.page - 1)}
                                                disabled={!pagination.has_prev || refreshing}
                                            >
                                                <FaChevronLeft />
                                            </Button>

                                            <span className="px-3">
                                                Page {pagination.page} of {pagination.total_pages}
                                            </span>

                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.page + 1)}
                                                disabled={!pagination.has_next || refreshing}
                                            >
                                                <FaChevronRight />
                                            </Button>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Container>

            {/* Status Change Modal */}
            <StatusChangeModal
                show={showStatusModal}
                onHide={() => setShowStatusModal(false)}
                onSubmit={handleStatusUpdate}
                patient={selectedPatientForAction}
                loading={actionLoading}
            />

            {/* Smart MedPass Dashboard Modal */}
            <SmartMedPassModal
                show={showSmartMedPassModal}
                onHide={() => setShowSmartMedPassModal(false)}
                patient={selectedPatientForMedPass}
            />

            {/* Custom Styles */}
            <style jsx="true">{`
                .patient-records-dashboard {
                    background-color: #f8f9fa;
                    min-height: 100vh;
                }

                .patient-card {
                    border-left: 4px solid #dee2e6;
                    transition: all 0.3s ease;
                }

                .patient-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
                    border-left-color: #007bff;
                }

                .patient-name {
                    font-size: 1.1rem;
                    line-height: 1.3;
                    color: #2c3e50;
                }

                .patient-mrn {
                    font-size: 0.9rem;
                    color: #6c757d;
                }

                .patient-id {
                    font-size: 0.8rem;
                    color: #6c757d;
                }

                .status-badge {
                    font-size: 0.75rem;
                    font-weight: 500;
                    padding: 0.25rem 0.5rem;
                    border-radius: 0.375rem;
                }

                .contact-info, .services, .last-visit, .physician-info {
                    font-size: 0.85rem;
                }

                .page-title {
                    color: #2c3e50;
                    font-weight: 600;
                }

                .pagination-info {
                    color: #6c757d;
                }

                /* EMR specific styling */
                .services .badge {
                    font-size: 0.7rem;
                    font-weight: 500;
                }

                .patient-details {
                    background-color: #f8f9fa;
                    padding: 0.75rem;
                    border-radius: 0.375rem;
                    margin-bottom: 1rem;
                }

                .quick-form-actions .dropdown-toggle {
                    font-size: 0.875rem;
                }

                .patient-actions-buttons .btn {
                    font-size: 0.8rem;
                    padding: 0.375rem 0.75rem;
                }

                @media (max-width: 768px) {
                    .patient-records-dashboard {
                        padding: 1rem !important;
                    }
                    
                    .patient-card {
                        margin-bottom: 1rem;
                    }

                    .patient-name {
                        font-size: 1rem;
                    }

                    .patient-details {
                        padding: 0.5rem;
                    }
                }
            `}</style>
        </>
    );
};

export default PatientRecordsDashboard; 